import { Theme } from "@mui/material";
import { ChartOptions } from "chart.js";
import { Currency } from "../../i18n/Currency";
import { percentageNoDecimals } from "../../shared/percentage";
import { getLegendLabels, getTicks } from "../../utils/chartjsTheming";

/** The height of a chart of risk, in px. */
export const RISK_CHART_HEIGHT = 320;

/** A line of a chart of risk shows no points until the pointer is over one. */
export const RISK_LINE_POINTS = {
  pointRadius: 0,
  pointHoverRadius: 4,
  pointHitRadius: 20,
} as const;

/**
 * The scales the charts of risk share: the cumulative probability of the
 * outcomes across, from none to all of it, and money up.
 */
export function riskChartScales({
  theme,
  currency,
  axisTitle,
  y,
}: {
  theme: Theme;
  currency: Currency;
  // says in what order the outcomes follow one another
  axisTitle: string;
  // how far the scale of the money reaches
  y: { min: number; max: number } | { beginAtZero: true };
}): NonNullable<ChartOptions<"line">["scales"]> {
  return {
    x: {
      type: "linear",
      min: 0,
      max: 1,
      title: {
        display: true,
        text: axisTitle,
        color: theme.palette.text.secondary,
        font: getTicks(theme).font,
      },
      grid: { display: false },
      ticks: {
        ...getTicks(theme),
        stepSize: 0.25,
        callback: (value) =>
          typeof value === "number"
            ? percentageNoDecimals.format(value)
            : value,
      },
    },
    y: {
      ...y,
      grid: {
        // the line of zero stands out from the lines of the other amounts
        color: (context) =>
          context.tick.value === 0
            ? theme.palette.text.disabled
            : theme.palette.divider,
      },
      ticks: {
        ...getTicks(theme),
        // the ends of the scale are no round amounts: named, they would
        // stand right beside the round ones next to them
        includeBounds: false,
        callback: (value) =>
          typeof value === "number" ? currency.formatCompact(value) : value,
      },
    },
  };
}

/** The legend of a chart of risk, at its upper right. */
export function riskChartLegend(theme: Theme) {
  return {
    position: "top",
    align: "end",
    labels: { ...getLegendLabels(theme), boxWidth: 18, boxHeight: 10 },
  } as const;
}
